import React, { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Paper,
  Box,
  Typography,
  TableContainer,
  Button,
  TextField,
  Divider,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { CSS } from "@dnd-kit/utilities";

import { useIsBusinessPlan } from "../../helpers/subscriptionPlan";

const sortableFields = [
  { key: "dateAsc", label: "Time(Old-New)" },
  { key: "dateDesc", label: "Time(New-Old)" },
  { key: "descriptionAsc", label: "Description(A-Z)" },
  { key: "keywordAsc", label: "Keyword(A-Z)" },
  { key: "skuAsc", label: "SKU(A-Z)" },
  { key: "detailAsc", label: "Detail(A-Z)" },
];

export const defaultInvoiceTable = [
  { key: "enablePrint", label: "Use Invoice", enabled: false, inTable: false },
  { key: "count", label: "Count", enabled: true, tableLabel: "#" },
  {
    key: "description",
    label: "Description",
    enabled: true,
    tableLabel: "Description",
  },
  {
    key: "descriptionWithImage",
    label: "Description + Keyword's Image",
    enabled: false,
    tableLabel: "Description",
  },
  {
    key: "descriptionWithQuantity",
    label: "Description + Quantity",
    enabled: false,
    tableLabel: "Description",
  },
  // {
  //   key: "sortItemAlphabetically",
  //   label: "Sort Items Alphabetically",
  //   enabled: false,
  //   inTable: false,
  // },
  {
    key: "sortBy",
    label: "Sort Items By",
    enabled: false,
    inTable: false,
    value: "dateAsc",
  },
  { key: "keyword", label: "Keyword", enabled: false, tableLabel: "Key" },
  { key: "sku", label: "Keyword's SKU", enabled: false, tableLabel: "SKU" },
  {
    key: "detail",
    label: "Keyword's Detail",
    enabled: false,
    tableLabel: "Detail",
  },
  {
    key: "unitPrice",
    label: "Unit Price",
    enabled: true,
    tableLabel: "Unit.P",
  },
  { key: "quantity", label: "Quantity", enabled: true, tableLabel: "Qty" },
  { key: "subtotal", label: "Subtotal", enabled: true },
  {
    key: "ordered",
    label: "CreatedAt",
    enabled: true,
    tableLabel: "Ordered",
  },
  { key: "notes", label: "Notes", enabled: true },
  { key: "summary", label: "Summary", enabled: true, inTable: false },
  {
    key: "footer",
    label: "Footer",
    enabled: false,
    inTable: false,
    value: "",
  },
  {
    key: "totalWeight",
    label: "Total Weight",
    enabled: false,
    inTable: false,
  },
  {
    key: "footerOrderBarcode",
    label: "Order Barcode",
    enabled: false,
    inTable: false,
  },
];

export const defaultDOTable = [
  {
    key: "enablePrint",
    label: "Use Delivery Order",
    enabled: true,
    inTable: false,
  },
  { key: "count", label: "Count", enabled: true, tableLabel: "#" },
  {
    key: "description",
    label: "Description",
    enabled: true,
    tableLabel: "Description",
  },
  {
    key: "descriptionWithImage",
    label: "Description + Keyword's Image",
    enabled: false,
    tableLabel: "Description",
  },
  {
    key: "descriptionWithQuantity",
    label: "Description + Quantity",
    enabled: false,
    tableLabel: "Description",
  },
  // {
  //   key: "sortItemAlphabetically",
  //   label: "Sort Items Alphabetically",
  //   enabled: false,
  //   inTable: false,
  // },
  {
    key: "sortBy",
    label: "Sort Items By",
    enabled: false,
    inTable: false,
    value: "dateAsc",
  },
  { key: "keyword", label: "Keyword", enabled: false, tableLabel: "Key" },
  { key: "sku", label: "Keyword's SKU", enabled: false, tableLabel: "SKU" },
  {
    key: "detail",
    label: "Keyword's Detail",
    enabled: false,
    tableLabel: "Detail",
  },
  {
    key: "unitPrice",
    label: "Unit Price",
    enabled: true,
    tableLabel: "Unit.P",
  },
  { key: "quantity", label: "Quantity", enabled: true, tableLabel: "Qty" },
  { key: "subtotal", label: "Subtotal", enabled: true },
  {
    key: "ordered",
    label: "CreatedAt",
    enabled: true,
    tableLabel: "Ordered",
  },
  { key: "notes", label: "Notes", enabled: true },
  { key: "summary", label: "Summary", enabled: false, inTable: false },
  {
    key: "footer",
    label: "Footer",
    enabled: false,
    inTable: false,
    value: "",
  },
  {
    key: "totalWeight",
    label: "Total Weight",
    enabled: false,
    inTable: false,
  },
  {
    key: "footerOrderBarcode",
    label: "Order Barcode",
    enabled: false,
    inTable: false,
  },
];

const previewData = [
  {
    count: 1,
    description: "Wireless Mouse",
    descriptionWithQuantity: "Wireless Mouse x1",
    descriptionWithImage: "Wireless Mouse 🖱️ [img]",
    keyword: "MOUSE",
    sku: "MOU123",
    unitPrice: 25.5,
    quantity: 2,
    subtotal: 51.0,
    ordered: "Aug 20",
    notes: "",
  },
  {
    count: 2,
    description: "Mechanical Keyboard",
    descriptionWithQuantity: "Mechanical Keyboard x2",
    descriptionWithImage: "Mechanical Keyboard ⌨️ [img]",
    keyword: "Keyboard",
    sku: "KEY987",
    unitPrice: 89.99,
    quantity: 1,
    subtotal: 89.99,
    ordered: "Aug 21",
    notes: "",
  },
];

const SortableRow = ({ id, children, disabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      hover
      {...(!disabled ? { ...attributes, ...listeners } : {})}
    >
      {children}
    </TableRow>
  );
};

const TableTemplate = ({ table, onChangeTable, label = "", defaultTable }) => {
  const [fields, setFields] = useState(table);
  const [open, setOpen] = useState(false);
  const enablePrint =
    fields.find((f) => f.key === "enablePrint")?.enabled ?? false;

  const isBusinessPlan = useIsBusinessPlan();

  // keep state in sync with prop
  useEffect(() => {
    setFields(table);
  }, [table]);

  // bubble up changes
  const updateParent = (newFields) => {
    setFields(newFields);
    if (onChangeTable) onChangeTable(newFields);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active?.id !== over?.id) {
      const oldIndex = fields.findIndex((i) => i.key === active.id);
      const newIndex = fields.findIndex((i) => i.key === over.id);
      const newFields = arrayMove(fields, oldIndex, newIndex);
      updateParent(newFields);
    }
  };

  const toggleEnabled = (key) => {
    updateParent(
      fields.map((f) => (f.key === key ? { ...f, enabled: !f.enabled } : f))
    );
  };

  // reset to defaults
  const handleResetDefault = () => {
    if (defaultTable) {
      updateParent(defaultTable);
    }
  };

  // filter out non-row fields
  const rowFields = fields.filter((f) => (f.inTable ?? true) && f.enabled);

  return (
    <Paper variant="outlined" sx={{ mt: 2, overflow: "hidden" }}>
      {/* Header row with button to open dialog */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2}
        py={1}
        sx={(theme) => ({
          bgcolor:
            theme.palette.mode === "dark"
              ? theme.palette.grey[800]
              : theme.palette.grey[100],
          color: theme.palette.text.primary,
        })}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {label || "Invoice Table Settings"}
        </Typography>
        <Button size="small" variant="outlined" onClick={() => setOpen(true)}>
          Configure
        </Button>
      </Box>

      {/* Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{label || "Invoice Table Settings"}</DialogTitle>
        <DialogContent dividers>
          {/* Drag + checkbox table */}
          <h3>Remember to save after edit</h3>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={fields.map((f) => f.key)}
              strategy={verticalListSortingStrategy}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width={50}></TableCell>
                    <TableCell>Field</TableCell>
                    <TableCell align="center">Enabled</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* --- Non-table fields (fixed, not sortable) --- */}
                  {fields
                    .filter((f) => f.inTable === false)
                    .map((field) => (
                      <TableRow key={field.key}>
                        <TableCell width={50}></TableCell>
                        <TableCell>{field.label}</TableCell>
                        <TableCell align="center">
                          <Checkbox
                            checked={field.enabled}
                            onChange={() => toggleEnabled(field.key)}
                            disabled={
                              !enablePrint && field.key !== "enablePrint"
                            }
                          />
                          {field.key === "footer" && field.enabled && (
                            <Box sx={{ mt: 1, textAlign: "left" }}>
                              <TextField
                                fullWidth
                                size="small"
                                placeholder="Enter footer text..."
                                value={field.value || ""}
                                disabled={!enablePrint}
                                onKeyDown={(e) => {
                                  if (e.code === "Space" || e.key === " ")
                                    e.stopPropagation();
                                }}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  updateParent(
                                    fields.map((f) =>
                                      f.key === "footer"
                                        ? { ...f, value: newValue }
                                        : f
                                    )
                                  );
                                }}
                              />
                            </Box>
                          )}
                          {field.key === "sortBy" && field.enabled && (
                            <Box sx={{ mt: 1, textAlign: "left" }}>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label="Sorting Option"
                                value={field.value || "dateAsc"} // fallback to dateAsc
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  updateParent(
                                    fields.map((f) =>
                                      f.key === "sortBy"
                                        ? { ...f, value: newValue }
                                        : f
                                    )
                                  );
                                }}
                                SelectProps={{
                                  native: true,
                                }}
                              >
                                {sortableFields.map((opt) => (
                                  <option key={opt.key} value={opt.key}>
                                    {opt.label}
                                  </option>
                                ))}
                              </TextField>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  {/* Divider between sections */}
                  <TableRow>
                    <TableCell colSpan={3}>
                      {/* Show restriction message for Hobbyist */}
                      {!isBusinessPlan && (
                        <Box sx={{ my: 2 }}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              bgcolor: "grey.50",
                              borderColor: "grey.200",
                              borderRadius: 2,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              You can only use default sorting and fields in the
                              table with the <strong>Hobbyist plan</strong>.{" "}
                              <br />
                              Upgrade to <strong>Business plan</strong> to fully
                              customize.
                            </Typography>
                          </Paper>
                        </Box>
                      )}

                      {/* Section title */}
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{ mt: 2, mb: 1, color: "text.secondary" }}
                      >
                        Sortable Fields
                      </Typography>

                      <Divider sx={{ mb: 1 }} />
                    </TableCell>
                  </TableRow>
                  {/* --- Table fields (sortable) --- */}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={fields
                        .filter((f) => f.inTable !== false)
                        .map((f) => f.key)}
                      strategy={verticalListSortingStrategy}
                    >
                      {fields
                        .filter((f) => f.inTable !== false)
                        .map((field) => (
                          <SortableRow
                            key={field.key}
                            id={field.key}
                            disabled={!isBusinessPlan}
                          >
                            <TableCell width={50}>
                              <IconButton
                                size="small"
                                sx={{ cursor: "grab" }}
                                disabled={!enablePrint || !isBusinessPlan}
                              >
                                <DragIndicatorIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                            <TableCell>{field.label}</TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={field.enabled}
                                onChange={() => toggleEnabled(field.key)}
                                disabled={
                                  (!enablePrint &&
                                    field.key !== "enablePrint") ||
                                  (!isBusinessPlan && field.inTable !== false)
                                }
                              />
                            </TableCell>
                          </SortableRow>
                        ))}
                    </SortableContext>
                  </DndContext>
                </TableBody>
              </Table>
            </SortableContext>
          </DndContext>

          {/* Preview */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Table Preview
            </Typography>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {rowFields.map((field) => (
                      <TableCell key={field.key}>
                        {field.tableLabel || field.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.map((row, i) => (
                    <TableRow key={i}>
                      {rowFields.map((field) => (
                        <TableCell key={field.key}>
                          {row[field.key] ?? ""}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetDefault} color="secondary">
            Reset Default
          </Button>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TableTemplate;
